import React, { useState } from 'react';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { BED_ZONES, useBedManagement } from '../contexts/FirestoreBedManagementContext';
import BedZone from './BedZone';
import PatientQueue from './PatientQueue';
import BedOccupancyStats from './BedOccupancyStats';
import { Users, Activity, Bed as BedIcon, AlertCircle, Wifi, WifiOff, RefreshCw } from 'lucide-react';

const BedManagementDashboard = ({ edPatients = [] }) => {
  const { beds, occupancyStats, assignBed, transferPatient, isConnected, connectionError, lastUpdate } = useBedManagement();
  const [selectedZone, setSelectedZone] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Filter unassigned ED patients
  const unassignedPatients = edPatients.filter(patient => 
    !beds.some(bed => bed.patient?.id === patient.id)
  );
  
  // Setup drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };
  
  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over) return;
    
    // Check if we're moving a patient to a bed
    if (active.id.startsWith('patient-') && over.id.startsWith('bed-')) {
      const patient = unassignedPatients.find(p => p.id === active.id);
      const bed = beds.find(b => b.id === over.id);
      
      if (patient && bed && bed.status === 'available') {
        // Validate ESI level matches zone
        const zone = Object.values(BED_ZONES).find(z => z.id === bed.zone);
        if (zone && zone.esiLevels.includes(patient.esiLevel)) {
          setIsProcessing(true);
          try {
            const result = await assignBed(bed.id, patient);
            if (result.success) {
              showNotification(`Patient ${patient.name} assigned to Bed ${bed.number}`, 'success');
            } else {
              showNotification(result.error || 'Failed to assign bed', 'error');
            }
          } catch (error) {
            showNotification('Network error: Failed to assign bed', 'error');
          } finally {
            setIsProcessing(false);
          }
        } else {
          showNotification(`ESI ${patient.esiLevel} patient cannot be assigned to ${zone.name} zone`, 'warning');
        }
      }
    }
    
    // Check if we're transferring between beds
    if (active.id.startsWith('bed-') && over.id.startsWith('bed-') && active.id !== over.id) {
      const fromBed = beds.find(b => b.id === active.id);
      const toBed = beds.find(b => b.id === over.id);
      
      if (fromBed?.patient && toBed?.status === 'available') {
        setIsProcessing(true);
        try {
          const result = await transferPatient(fromBed.id, toBed.id);
          if (result.success) {
            showNotification(`Patient transferred from Bed ${fromBed.number} to Bed ${toBed.number}`, 'success');
          } else {
            showNotification(result.error || 'Failed to transfer patient', 'error');
          }
        } catch (error) {
          showNotification('Network error: Failed to transfer patient', 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    }
  };
  
  // Filter beds by search
  const filteredBeds = searchTerm 
    ? beds.filter(bed => 
        bed.number.toString().includes(searchTerm) ||
        bed.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bed.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : beds;
  
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Connection Status Bar */}
      <div className={`fixed top-0 left-0 right-0 h-1 transition-all ${
        isConnected ? 'bg-green-500' : 'bg-red-500'
      }`} />
      
      {/* Header */}
      <div className="bg-white rounded-lg shadow mb-6 p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Emergency Department Bed Management</h1>
            <p className="text-sm text-gray-500 mt-1">Real-time bed status and patient assignment</p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Connection Status */}
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
              isConnected 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 text-sm font-medium">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 text-sm font-medium">
                    {connectionError || 'Disconnected'}
                  </span>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1">
              <Activity className="h-4 w-4 text-blue-600" />
              <span className="text-blue-700 text-sm font-medium">
                {occupancyStats.occupancyRate}% Occupancy
              </span>
            </div>
            <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1">
              <BedIcon className="h-4 w-4 text-green-600" />
              <span className="text-green-700 text-sm font-medium">
                {occupancyStats.available} Available
              </span>
            </div>
          </div>
        </div>
        
        {/* Occupancy Stats */}
        <BedOccupancyStats stats={occupancyStats} />
        
        {/* Last Update */}
        {lastUpdate && (
          <div className="mt-4 text-sm text-gray-500">
            Last update: {new Date(lastUpdate.timestamp).toLocaleTimeString()}
          </div>
        )}
      </div>
      
      {/* Zone Filters and Search */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedZone('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedZone === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Zones ({beds.length})
            </button>
            {Object.entries(BED_ZONES).map(([key, zone]) => (
              <button
                key={zone.id}
                onClick={() => setSelectedZone(zone.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedZone === zone.id
                    ? 'text-white'
                    : 'text-gray-700 hover:opacity-80'
                }`}
                style={{
                  backgroundColor: selectedZone === zone.id ? zone.color : '#f3f4f6'
                }}
              >
                {zone.name} ({zone.capacity})
              </button>
            ))}
          </div>
          
          <input
            type="text"
            placeholder="Search bed or patient..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      
      {/* Main Content with DnD Context */}
      <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Bed Board (3 columns) */}
          <div className="lg:col-span-3">
            <div className="space-y-6">
              {Object.entries(BED_ZONES).map(([key, zone]) => {
                if (selectedZone !== 'all' && selectedZone !== zone.id) return null;
                
                const zoneBeds = filteredBeds.filter(bed => bed.zone === zone.id);
                if (searchTerm && zoneBeds.length === 0) return null;
                
                return (
                  <BedZone
                    key={zone.id}
                    zone={zone}
                    beds={zoneBeds}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Patient Queue (1 column) */}
          <div className="lg:col-span-1">
            <PatientQueue patients={unassignedPatients} />
          </div>
        </div>
      </DndContext>
      
      {/* Alert for unassigned critical patients */}
      {unassignedPatients.filter(p => p.esiLevel === 1).length > 0 && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-lg shadow-lg flex items-center space-x-2">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">
            {unassignedPatients.filter(p => p.esiLevel === 1).length} ESI-1 patients awaiting bed assignment
          </span>
        </div>
      )}
      
      {/* Processing overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
            <span className="text-gray-700">Processing bed assignment...</span>
          </div>
        </div>
      )}
      
      {/* Notifications */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all ${
          notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
          notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
          notification.type === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
          'bg-blue-50 border border-blue-200 text-blue-800'
        }`}>
          <div className="flex items-center space-x-2">
            {notification.type === 'success' && <Activity className="h-5 w-5" />}
            {notification.type === 'error' && <AlertCircle className="h-5 w-5" />}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BedManagementDashboard; 